import {
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC
} from "react";
import {
    type LayoutChangeEvent,
    type PointerEvent,
    Animated,
    View
} from "react-native";
import type IScrollAreaBarProps from "./type";
import type {
    ScrollAreaBarDragTargetType,
    ScrollAreaBarDragEventType
} from "./type";
import stylesheet, {
    getScrollAreaThumb,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useReducedMotion
} from "../../../../helpers/motion";
import {
    getScrollAreaMaxOffset
} from "../../util";

const dragTarget = globalThis as unknown as ScrollAreaBarDragTargetType;

const ScrollAreaBar: FC<IScrollAreaBarProps> = ({
    isCornerReserved,
    contentLength,
    layoutLength,
    customTheme,
    onScrollTo,
    getOffset,
    isVisible,
    offset,
    axis
}) => {
    const {
        shapeConfigs,
        durations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const isReduceMotionEnabled = useReducedMotion();

    const [
        trackLength,
        setTrackLength
    ] = useState(0);

    const [
        isHovered,
        setIsHovered
    ] = useState(false);

    const [
        isDragging,
        setIsDragging
    ] = useState(false);

    const removeDragListenersRef = useRef<(() => void) | undefined>(undefined);

    const isVertical = axis === "vertical";

    const maxOffset = getScrollAreaMaxOffset(contentLength, layoutLength);

    const thumb = getScrollAreaThumb({
        minLength: spaces.spacingXl,
        contentLength,
        layoutLength,
        trackLength
    });

    const isShown = isVisible || isHovered || isDragging;

    const translate = useMemo(() => {
        if(maxOffset <= 0 || thumb.travel <= 0) {
            return 0;
        }

        return offset.interpolate({
            extrapolate: "clamp",
            outputRange: [
                0,
                thumb.travel
            ],
            inputRange: [
                0,
                maxOffset
            ]
        });
    }, [
        thumb.travel,
        maxOffset,
        offset
    ]);

    const {
        track: trackDynamicStyle,
        thumb: thumbDynamicStyle
    } = useStyles({
        thumbLength: thumb.length,
        isReduceMotionEnabled,
        isCornerReserved,
        shapeConfigs,
        isDragging,
        durations,
        isHovered,
        radiuses,
        borders,
        isShown,
        colors,
        axis
    });

    useEffect(() => {
        return () => {
            removeDragListenersRef.current?.();
        };
    }, []);

    const getClampedOffset = (value: number) => {
        return Math.min(maxOffset, Math.max(0, value));
    };

    const handleDragStart = (startPointer: number) => {
        removeDragListenersRef.current?.();

        const startOffset = getOffset();

        const ratio = maxOffset / thumb.travel;

        const onPointerMove = (event: ScrollAreaBarDragEventType) => {
            const pointer = isVertical ? event.clientY : event.clientX;

            onScrollTo(getClampedOffset(startOffset + (pointer - startPointer) * ratio), false);
        };

        const onPointerEnd = () => {
            removeDragListenersRef.current?.();
        };

        dragTarget.addEventListener("pointercancel", onPointerEnd);

        dragTarget.addEventListener("pointermove", onPointerMove);

        dragTarget.addEventListener("pointerup", onPointerEnd);

        removeDragListenersRef.current = () => {
            dragTarget.removeEventListener("pointercancel", onPointerEnd);

            dragTarget.removeEventListener("pointermove", onPointerMove);

            dragTarget.removeEventListener("pointerup", onPointerEnd);

            removeDragListenersRef.current = undefined;

            setIsDragging(false);
        };

        setIsDragging(true);
    };

    const handlePointerDown = (event: PointerEvent) => {
        if(event.nativeEvent.button !== 0 || maxOffset <= 0 || thumb.travel <= 0) {
            return;
        }

        event.preventDefault();

        const position = isVertical ? event.nativeEvent.offsetY : event.nativeEvent.offsetX;

        const currentOffset = getOffset();

        const thumbStart = currentOffset / maxOffset * thumb.travel;

        if(position >= thumbStart && position <= thumbStart + thumb.length) {
            handleDragStart(isVertical ? event.nativeEvent.clientY : event.nativeEvent.clientX);

            return;
        }

        const step = position < thumbStart ? -layoutLength : layoutLength;

        onScrollTo(getClampedOffset(currentOffset + step), !isReduceMotionEnabled);
    };

    const handleLayout = (event: LayoutChangeEvent) => {
        const {
            height,
            width
        } = event.nativeEvent.layout;

        setTrackLength(isVertical ? height : width);
    };

    const handlePointerEnter = () => {
        setIsHovered(true);
    };

    const handlePointerLeave = () => {
        setIsHovered(false);
    };

    return <View
        style={[
            stylesheet.track,
            isVertical ? stylesheet.verticalTrack : stylesheet.horizontalTrack,
            trackDynamicStyle
        ]}
        onPointerLeave={handlePointerLeave}
        onPointerEnter={handlePointerEnter}
        onPointerDown={handlePointerDown}
        onLayout={handleLayout}
        aria-hidden={true}
    >
        <Animated.View
            style={[
                stylesheet.thumb,
                isVertical ? stylesheet.verticalThumb : stylesheet.horizontalThumb,
                thumbDynamicStyle,
                {
                    transform: isVertical ? [
                        {
                            translateY: translate
                        }
                    ] : [
                        {
                            translateX: translate
                        }
                    ]
                }
            ]}
        />
    </View>;
};
export default ScrollAreaBar;
